/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import {
  IBundle,
  IBundle_Entry,
  ICodeableConcept,
  ICoding,
  IDiagnosticReport,
  IDocumentReference,
  IOperationOutcome,
} from '@ahryman40k/ts-fhir-types/lib/R4';
import { analyticsTrack } from '../../analytics_utils';
import { PearlUser } from '../../Login/userSlice';
import { makePearlApiFhirCallAndUnwrap, makePearlApiFhirCallAndUnwrapPaged, throwIfAborted } from '../fhir_network';
import { IObservationSimplified } from '../fhir_types';
import { getCpt4Coding, getLoincCoding } from '../fhir_utils';
import { parseHL7Date, parseUSDateTimeString, xmlToIObservation } from './utils';

/** We want to map this to IDiagnosticReportSimplified */
export type CareEverywherePanel = Pick<IDiagnosticReport, 'effectiveDateTime' | 'id' | 'code' | 'resourceType'> & {
  id2?: string | null; // e.g. procedure256. Only used temporarily to link to the procedure table. Nothing to do with CPT or anything
  resourceType: 'DiagnosticReport';
  isCareEverywhere: true;
  result?: IObservationSimplified[];
};

/**
 * Parses a Care Everywhere C-CDA XML string and extracts lab panels and their components
 * from the "Relevant diagnostic tests/laboratory data Narrative" section (LOINC 30954-2).
 *
 * @param xml - The raw XML string from a DocumentReference Binary.
 * @returns An array of CareEverywherePanel objects representing lab panels with metadata and results.
 */
export function parseCareEverywhereResponse(xml: string): {
  careEverywhereData: CareEverywherePanel[];
  numCareEverywhereRecordsRead: number;
} {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, 'text/xml');

  const errorNode = doc.querySelector('error');
  if (errorNode) {
    throw new Error(`Error from Care Everywhere: ${errorNode.textContent}`);
  }
  const res: CareEverywherePanel[] = [];
  const numCareEverywhereRecordsRead = doc.querySelector('MultipleCareEverywhereResponse')?.getAttribute('count');
  // First, parse the results table.  This will have the Observation info for the panel, but the title might not be quite right and it won't have CPT codes
  parseCareEverywhereResultsTable_(doc, res);
  parseCareEverywhereObservationTable_(doc, res); // Get units for the observations
  // Next, parse the procedures table.  This will have the procedure name and date, but not the CPT codes
  parseCareEverywhereProceduresTable_(doc, res);
  // Get the CPT codes from the entry table.  This will have the CPT codes and the reference to the procedure table
  parseCareEverywhereEntryTable_(doc, res);
  return {
    careEverywhereData: res,
    numCareEverywhereRecordsRead: numCareEverywhereRecordsRead ? parseInt(numCareEverywhereRecordsRead, 10) : 1,
  };
}

/**
 * This has observation data
 */
function parseCareEverywhereResultsTable_(doc: Document, panels: CareEverywherePanel[]) {
  const labSection = doc.querySelector('section > code[code="30954-2"]')?.parentElement;
  if (!labSection) return;

  const items = labSection.querySelectorAll('item');

  items.forEach((item) => {
    const id = item.getAttribute('ID');

    const caption = item.querySelector('caption')?.textContent?.trim();
    const rawText = caption || item.childNodes[0]?.textContent?.trim() || 'Unnamed Panel';
    const name = rawText
      .replace(/\(ABNORMAL\)/i, '')
      .replace(/\(\d{2}\/\d{2}\/\d{4}.*?\)/, '')
      .trim();

    const dateMatch = rawText.match(/\d{2}\/\d{2}\/\d{4} \d{1,2}:\d{2} (?:AM|PM) [A-Z]+/)?.[0];
    const effectiveDateTime = dateMatch ? parseUSDateTimeString(dateMatch)?.toISOString() : undefined;

    const tables = item.querySelectorAll('table');
    if (!tables.length) return;

    const rows = tables[0]!.querySelectorAll('tbody > tr');
    const result: CareEverywherePanel['result'] = [];

    rows.forEach((row) => {
      /** This is a row of observation data. It looks like:
                  <tr ID="Result.1.2.840.114350.1.13.297.3.7.2.798268.2063247.Comp1" styleCode="xRowNormal">
                    <td ID="Result.1.2.840.114350.1.13.297.3.7.2.798268.2063247.Comp1Name">INR, POC</td>
                    <td>
                      <content styleCode="xflagData">5.0</content>
                      <content styleCode="xflagData"> (A)</content>
                    </td>
                    <td>0.9 - 1.2</td>
                    <td/>
                    <td/>
                    <td/>
                    <td ID="Result.1.2.840.114350.1.13.297.3.7.2.798268.2063247.Comp1Signature"/>
                  </tr>
        Note that it doesn't have the units in this table. We need to save the tr ID, and get the units from another table.
       */
      const id = row.getAttribute('ID') || undefined;
      const cells = row.querySelectorAll('td');
      if (cells.length < 3) return;

      const component = cells[0]?.textContent?.trim() ?? '';
      if (!component || /instrument id/i.test(component)) return;

      const value =
        Array.from(cells[1]!.querySelectorAll('content'))
          .map((c) => c.textContent?.trim())
          .filter(Boolean)
          .join(' ') ||
        cells[1]?.textContent?.trim() ||
        '';

      const refRange = cells[2]?.textContent?.trim();

      const codeElement = row.querySelector('code[codeSystem="2.16.840.1.113883.6.1"]');
      const code = codeElement?.getAttribute('code') ?? undefined;

      result.push({
        resourceType: 'Observation',
        id: id || undefined,
        code: {
          text: component,
          ...(code
            ? {
                coding: [
                  {
                    system: 'http://loinc.org',
                    code,
                  },
                ],
              }
            : {}),
        },
        valueString: value || undefined, // this may get overwritten later if you find the units and type
        referenceRange: refRange
          ? [
              {
                text: refRange,
              },
            ]
          : undefined,
      });
    });

    if (result.length > 0) {
      const code: ICodeableConcept = {
        text: name,
        coding: [], // CPT Will be added later
      };
      panels.push({
        id: id || undefined,
        id2: null,
        code,
        effectiveDateTime,
        result,
        resourceType: 'DiagnosticReport',
        isCareEverywhere: true,
      });
    }
  });
}

/** In CareEverywhere this looks like:
  <code code="47519-4" codesystem="2.16.840.1.113883.6.1" codesystemname="LOINC" displayname="History of Procedures Document">
    <title>Procedures</title><text>
      <table>
        <thead><tr><th>Procedure Name</th><th>Priority</th><th>Date/Time</th><th>Associated Diagnosis</th><th>Comments</th></tr></thead>
        <tbody>
        <tr id="procedure256" stylecode="xRowNormal">
          <td><content id="procedure256name">POCT FINGERSTICK INR</content></td>
          <td>Routine</td>
          <td>04/01/2025 10:46 AM PDT</td>
          <td><paragraph>Deviated septum</paragraph></td>
          <td><paragraph stylecode="xHeader">Results for this procedure are in the <content stylecode="xLink2-Result.1.2.840.114350.1.13.297.3.7.2.798268.2063247">results section</content>.</paragraph></td>
        </tr>

  Get this data into the panels array.
  Note there's no Observation data here, and no cpt code
 *
*/
function parseCareEverywhereProceduresTable_(doc: Document, panels: CareEverywherePanel[]) {
  const proceduresSection = doc.querySelector('section > code[code="47519-4"]')?.parentElement;
  if (!proceduresSection) return;

  const trs = proceduresSection.querySelectorAll('tbody > tr');
  trs.forEach((tr) => {
    // Each row is a procedure - like a DiagnosticPanel
    const cells = tr.querySelectorAll('td');
    if (cells.length < 2) return;

    const procedureName = cells[0]?.textContent?.replace(/\s+/g, ' ').trim() ?? '';
    const dateStr = cells[2]?.textContent?.trim();
    const date = dateStr ? new Date(dateStr) : undefined;
    // Get the ID from the <content stylecode="xLink2-Result.1.2.840.114350.1.13.297.3.7.2.798268.2063247"> element
    const id = cells[4]?.querySelector('content[styleCode]')?.getAttribute('styleCode')?.replace('xLink2-', '') ?? null;
    const id2 = tr.getAttribute('ID');

    let panel = id ? panels.find((p) => p.id === id) : null;
    if (!panel) {
      panel = {
        id: id || undefined,
        resourceType: 'DiagnosticReport',
        id2,
        code: {
          coding: [], // We don't have the CPT code yet
          text: procedureName,
        },
        effectiveDateTime: date?.toISOString(),
        result: [],
        isCareEverywhere: true,
      };
      panels.push(panel);
    } else {
      panel.effectiveDateTime = date?.toISOString() || panel.effectiveDateTime;
      panel.code.text = procedureName || panel.code.text;
      panel.id2 = id2;
    }
  });
}

/** Assumes we've aleady run parseCareEverywhereProceduresTable_.
 *  That will have fetched the observations but not the units.
 *
 *  Observation will look like:

    <observation xmlns="urn:hl7-org:v3" classCode="OBS" moodCode="EVN">
      <templateId root="2.16.840.1.113883.10.20.22.4.2"/>
      <templateId root="2.16.840.1.113883.10.20.22.4.2" extension="2015-08-01"/>
      <id root="1.2.840.114350.1.13.297.3.7.6.798268.2000" extension="1051152.10"/>
      <code code="17861-6" codeSystem="2.16.840.1.113883.6.1" codeSystemName="LOINC">
        <originalText>Calcium<reference value="#Result.1.2.840.114350.1.13.297.3.7.2.798268.1051152.Comp10Name"/></originalText>
        <translation code="1558170" codeSystem="1.2.840.114350.1.13.297.3.7.2.768282" codeSystemName="Epic.LRR.ID" displayName="Calcium"/>
      </code>
      <text>
        // This is how we find this observation
        <reference value="#Result.1.2.840.114350.1.13.297.3.7.2.798268.1051152.Comp10"/>
      </text>
      <statusCode code="completed"/>
      <effectiveTime value="20200731173934+0000"/>
      <value xsi:type="PQ" value="9.6" unit="mg/dL" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"/>
      <referenceRange>
        <observationRange>
          <text>8.3 - 10.4</text>
          <value xsi:type="IVL_PQ" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
            <low value="8.3" unit="mg/dL"/>
            <high value="10.4" unit="mg/dL"/>
          </value>
          <interpretationCode code="N" codeSystem="2.16.840.1.113883.5.83"/>
        </observationRange>
      </referenceRange>
    </observation>

 */
function parseCareEverywhereObservationTable_(doc: Document, panels: CareEverywherePanel[]) {
  // Loop over the existing panels
  panels.forEach((panel) => {
    panel.result?.forEach((component) => {
      if (!component.id) return;
      const obs = doc.querySelector(`component > observation text > reference[value="#${component.id}"]`)?.parentElement
        ?.parentElement;
      if (!obs) {
        console.warn(`No observation found for ${component.id}`);
        return;
      }
      xmlToIObservation(obs, component);
    });
  });
}

/**
 * This table appears to be the most reliable place to get the panel name and date, and CPT codes,
 * and is real XML data (not just a table). Looks like:
 *
  <component>
    <section>
      <code code="47519-4" codeSystem="2.16.840.1.113883.6.1" codeSystemName="LOINC" displayName="History of Procedures Document" />
      <table>... (procedure table parsed in above function) ...</table>
      <entry>
        <procedure classCode="PROC" moodCode="EVN">
          <code code="80069" codeSystem="2.16.840.1.113883.6.12" codeSystemName="CPT-4">
            <originalText>HC RENAL PANEL<reference value="#procedure263name" />
            </originalText>
          </code>
          <text>
            <reference value="#procedure256" />
          </text>
          <statusCode code="completed" />
          <effectiveTime value="2025-04-01T10:46:00-07:00" />

 We want to get the CPT code, and the reference value to map to id2 in the Procedures table.
 */
function parseCareEverywhereEntryTable_(doc: Document, panels: CareEverywherePanel[]) {
  const section = doc.querySelector('section > code[code="47519-4"]')?.parentElement;
  if (!section) return;
  const items = section.querySelectorAll('entry > procedure');

  items.forEach((item) => {
    // First get CPT code and original text (display)
    const cptCoding_ =
      item.querySelector('code[codeSystem="2.16.840.1.113883.6.12"]') ||
      item.querySelector('code translation[codeSystem="2.16.840.1.113883.6.1"]');
    const cptCode = cptCoding_?.getAttribute('code') ?? undefined;
    const cptOriginalText =
      (cptCoding_?.getAttribute('originalText') ||
        cptCoding_?.getAttribute('displayName') ||
        cptCoding_?.querySelector('originalText')?.textContent?.trim()) ??
      '';
    // Get the LOINC code if it exists
    let loincCoding_ =
      item.querySelector('code[codeSystem="2.16.840.1.113883.6.1"]') ||
      item.querySelector('code[codeSystemName="LOINC"]') ||
      item.querySelector('code translation[codeSystem="2.16.840.1.113883.6.1"]');
    let loincCode = loincCoding_?.getAttribute('code') ?? undefined;
    let loincDisplay =
      loincCoding_?.getAttribute('originalText') ??
      loincCoding_?.getAttribute('displayName') ??
      loincCoding_?.querySelector('originalText')?.textContent?.trim() ??
      undefined;

    const id2 = item?.querySelector(':scope > text > reference')?.getAttribute('value')?.replace('#', '') ?? null;
    const dateStr = item.querySelector(':scope > effectiveTime')?.getAttribute('value');
    const date = dateStr ? parseHL7Date(dateStr) : undefined;
    const panel = id2 ? panels.find((p) => p.id2 === id2) : undefined;

    const cptCoding: ICoding = {
      system: 'urn:oid:2.16.840.1.113883.6.12',
      code: cptCode || '',
      display: cptOriginalText || '',
    };
    const loincCoding: ICoding = {
      system: 'http://loinc.org',
      code: loincCode || '',
      display: loincDisplay || '',
    };
    const coding: ICoding[] = [];
    if (cptCode) coding.push(cptCoding);
    if (loincCode) coding.push(loincCoding);

    if (panel) {
      panel.code.coding = panel.code.coding || [];
      coding.forEach((c) => panel.code.coding!.push(c));
      panel.code.text ||= cptOriginalText || 'Unknown Panel';
    } else {
      panels.push({
        id: undefined,
        id2,
        effectiveDateTime: date?.toISOString(),
        code: { coding },
        result: [],
        resourceType: 'DiagnosticReport',
        isCareEverywhere: true,
      });
    }
  });
}

/** Requires permissions:
 *   - DocumentReference.Search (External CCDA)
 *   - Binary.Read (External CCDA)
 *  I would PRESUME that it also requires:
 *   - DocumentReference.Read permission. We don't use directly, but maybe DocumentReference.Search does?
 */
export async function fetchCareEverywhereRaw(
  patientId: string,
  getAllRecords: boolean,
  mrn: string,
  server: string,
  signal?: AbortSignal
): Promise<{
  result: string;
  sizeKb: number;
  url: string;
  error?: string;
}> {
  const str = `DocumentReference?patient=${patientId}&category=external-ccda`;

  let res = getAllRecords
    ? // Get all the paged results - we need all of them. Much slower.
      await makePearlApiFhirCallAndUnwrapPaged('/fhir/' + str, mrn, server, signal)
    : // We don't want to get the paged results, because we only need the first (latest) one
      await makePearlApiFhirCallAndUnwrap<IBundle | IOperationOutcome>('/fhir/' + str, mrn, server, signal);
  if (res?.resourceType === 'OperationOutcome') {
    return {
      error: res.issue?.[0]?.diagnostics,
      url: str,
      result: JSON.stringify(res, null, 2),
      sizeKb: JSON.stringify(res).length / 1024,
    };
  }

  let entries = res?.entry
    ?.map((e: IBundle_Entry) => e.resource)
    ?.filter(
      (r) =>
        r?.resourceType === 'DocumentReference' &&
        r?.docStatus === 'final' &&
        r?.type?.coding?.find((c) => c.system === 'Epic.DocumentType' && c.code === '1')
    ) as IDocumentReference[] | undefined;
  entries?.sort?.((a, b) => {
    const aDate = new Date(a?.date || 0);
    const bDate = new Date(b?.date || 0);
    return bDate.getTime() - aDate.getTime(); // Sort by date descending
  });

  if (!entries || entries.length === 0) {
    return { url: str, result: '', sizeKb: 0 };
  }
  async function parseEntry(entry: IDocumentReference) {
    const binaryUrl = (entry as IDocumentReference)?.content?.[0]?.attachment?.url;
    const s = JSON.stringify(res, null, 2);
    if (!binaryUrl) {
      return { result: s, sizeKb: s.length / 1024, error: 'No binary URL found', url: str };
    }
    // Now fetch the binary data
    console.log('fetching: binaryUrl', binaryUrl);
    const res2 = (await makePearlApiFhirCallAndUnwrap('/fhir/' + binaryUrl, mrn, server, signal)) as string;
    return { result: res2, sizeKb: (s.length + res2.length) / 1024, url: binaryUrl };
  }
  if (!getAllRecords) {
    const entry = entries[0]!;
    return await parseEntry(entry);
  } else {
    const combinedResults: { result: string; sizeKb: number; url: string } = { result: '', sizeKb: 0, url: str };
    // Remove duplicate context.period, keeping the most recent one
    const seenContextPeriod = new Set<string>();
    entries = entries.filter((entry) => {
      const period = `${entry?.context?.period?.start}-${entry?.context?.period?.end}`;
      if (seenContextPeriod.has(period)) {
        return false; // Skip duplicates
      }
      seenContextPeriod.add(period);
      return true; // Keep unique entries
    });
    console.log(`Found ${entries.length} unique entries to process.`, entries);
    for (const entry of entries) {
      const parsed = await parseEntry(entry);
      if (parsed.error) {
        return parsed; // Return the first error encountered
      }
      if (parsed.result) {
        combinedResults.result += parsed.result;
        combinedResults.sizeKb += parsed.sizeKb;
      }
    }
    // result needs to be xml, so wrap it in a root element
    combinedResults.result = `<MultipleCareEverywhereResponse count="${entries.length}">${combinedResults.result}</MultipleCareEverywhereResponse>`;
    return combinedResults;
  }
}

/** Fetch CareEverywhere data by:
 *   - First fetching the DocumentReference resources for the patient
 *   - Then fetching the Binary content for the latest DocumentReference
 *
 * With tests, getAllRecords being true still only fetches one usually, since they all have the same context.period.
 * With testing, not all the CareEverywhere records are available, and I don't know why.  In the first real patient
 * tested, there was no data from 2023 even though there were records in the Epic system.  - John 2025-05-30
 */
export async function fetchCareEverywhere(
  patientId: string,
  getAllRecords: boolean, // If true, fetch all records with DIFFERING context periods, otherwise just the latest one.
  mrn: string,
  server: string,
  signal?: AbortSignal,
  cb?: (careEverwhereData: CareEverywherePanel[]) => void
) {
  const raw = await fetchCareEverywhereRaw(patientId, getAllRecords, mrn, server, signal);
  throwIfAborted(signal);
  if (raw.error) {
    throw new Error(`Error fetching Care Everywhere data: ${raw.error}`);
  }
  if (!raw.result) {
    return []; // No data found
  }

  const res = parseCareEverywhereResponse(raw.result);
  if (cb) {
    cb(res.careEverywhereData);
  }
  return res.careEverywhereData;
}

export function analyticsTrackCareEverywhereData({
  careEverywhereData,
  pearlUserInfo,
}: {
  careEverywhereData: CareEverywherePanel[];
  pearlUserInfo: PearlUser;
}) {
  // One event per Care Everywhere panel
  careEverywhereData.forEach((panel) => {
    // Get the CPT code for the panel
    const cptCoding = getCpt4Coding(panel.code.coding);
    const loincCoding = getLoincCoding(panel.code.coding);
    if (cptCoding?.code || loincCoding?.code) {
      analyticsTrack(
        'care_everywhere_panel',
        {
          cptCode: cptCoding?.code || '',
          cptDisplay: cptCoding?.display || '',
          loincCode: loincCoding?.code || '',
          loincDisplay: loincCoding?.display || '',
          text: panel.code.text,
        },
        pearlUserInfo
      );
    }
  });
}
